import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Snackbar } from '@mui/material';
import { selectDesiredCreditsAmount } from '../../../../store/account/selectors';
import CreditsPrice from '../../../MyApps/components/CreditsPrice';
import FeaturesList from '../FeaturesList';
import { Feature } from '../FeaturesList/FeaturesList';
import FeedbackModal from '../../../FeedbackModal';
import {
  StyledButton,
  StyledCard,
  StyledCardActions,
  StyledCardContent,
  StyledCardHeader,
  StyledPurchaseNotice,
} from './styled';

const FEATURES_LIST: Feature[] = [
  { label: 'Custom pricing available' },
  { label: 'Dynamic allowance and billing' },
  { label: 'Super-fast music generation' },
  { label: 'Access all core models' },
  { label: 'Text-to-music' },
  { label: 'Music Generator' },
  { label: 'Music Catalog' },
  { label: 'Sub-license' },
];

const PRICE_SCALE = [
  { label: 'Basic', amount: 500, price: 0.15 },
  { label: 'Volume A', amount: 1000, price: 0.125 },
  { label: 'Volume B', amount: 3000, price: 0.113 },
  { label: 'Volume C', amount: 5000, price: 0.101 },
  { label: 'Volume D', amount: 10000, price: 0.091 },
  { label: 'Volume E', amount: 15000, price: 0.087 },
  { label: 'Volume F', amount: 20000, price: 0.082 },
  { label: 'Volume G', amount: 25000, price: 0.078 },
  { label: 'Volume H', amount: 30000, price: 0.074 },
  { label: 'Volume I', amount: 40000, price: 0.073 },
  { label: 'Volume N', amount: 50000, price: 0.071 },
];

const calculatePricePerUnit = (
  desiredAmount: number,
  priceTiers: {
    label: string;
    amount: number;
    price: number;
  }[]
): number => {
  if (desiredAmount <= 0) {
    return 0;
  }

  for (let i = priceTiers.length - 1; i >= 0; i--) {
    if (desiredAmount >= priceTiers[i].amount) {
      return priceTiers[i].price;
    }
  }

  return priceTiers[0].price;
};

const PurchaseSubscription = () => {
  const desiredCreditsAmount = useSelector(selectDesiredCreditsAmount);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isSnackbarShown, setIsSnackbarShown] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  const pricePerCredit = useMemo(() => {
    if (!PRICE_SCALE.length || !desiredCreditsAmount) {
      return null;
    }
    return calculatePricePerUnit(desiredCreditsAmount, PRICE_SCALE);
  }, [PRICE_SCALE, desiredCreditsAmount]);

  const handleOpenFeedbackModal = () => {
    setIsFeedbackModalOpen(true);
  };

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
  };

  const handleFeedbackSuccess = () => {
    setIsFeedbackModalOpen(false);

    setSnackbarMessage(
      'Email sent successfully! Thank you for your interest. Our sales team will get back to you as soon as possible.'
    );
    setIsSnackbarShown(true);
  };

  const handleFeedbackError = () => {
    setSnackbarMessage('Something went wrong. Please try again later.');
    setIsSnackbarShown(true);
  };

  if (pricePerCredit === null) {
    return null;
  }

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={isSnackbarShown}
        autoHideDuration={6000}
        onClose={() => setIsSnackbarShown(false)}
        message={snackbarMessage}
      />
      <StyledCard>
        <StyledCardHeader
          action="Best Value"
          title="Subscription Plan"
          subheader="Monthly subscription with volume discounts. Great for scale-ups and dynamic usage."
        />
        <StyledCardContent>
          <CreditsPrice
            pricePerCredit={pricePerCredit}
            labels={{
              totalUnits: 'Monthly track generations',
              costPerUnit: 'Cost per track generation',
              total: 'Recurring monthly payment',
            }}
          />
          <FeaturesList features={FEATURES_LIST} />
        </StyledCardContent>
        <StyledCardActions>
          <StyledPurchaseNotice>
            All Subcription partners are automatically protected with corporate
            indemnification.
          </StyledPurchaseNotice>
          <StyledButton onClick={handleOpenFeedbackModal}>
            Contact Sales
          </StyledButton>
        </StyledCardActions>
      </StyledCard>
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={handleCloseFeedbackModal}
        onSuccess={handleFeedbackSuccess}
        onError={handleFeedbackError}
      />
    </>
  );
};

export default PurchaseSubscription;
